import { cx } from "@emotion/css";
import dayjs from "dayjs";
import { ChevronDown, CircleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
} from "src/components/Default/LekUI/Accordion";
import {
	OutlinedPrimaryButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { Filters } from "src/components/Default/LekUI/Filters";
import { Sort } from "src/components/Default/LekUI/Sort";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { useAiResponseReviews, useUpdateAiReviewStatus } from "src/hooks/useAi";
import { useFontSize } from "src/hooks/useFontSize";
import { isStaff } from "src/hooks/useUser";

type ReviewStatus = "waiting" | "resolved";
type StatusFilter = ReviewStatus | "all";
type SortBy = "createdAt" | "updatedAt" | "resolvedAt";
type SortOrder = "asc" | "desc";

interface ReviewUser {
	id: number;
	username: string;
	avatarUrl: string | null;
	isBanned?: boolean;
	isStaff?: boolean;
}

interface Review {
	id: number;
	aiResponseId: number;
	userId: number;
	review: string;
	status: ReviewStatus;
	resolvedBy: string | null;
	resolvedAt: string | null;
	createdAt: string;
	updatedAt: string;
	user: ReviewUser | null;
}

interface AiResponseReviewsProps {
	responseId: number;
}

const DEFAULT_STATUS: StatusFilter = "all";
const DEFAULT_SORT_BY: SortBy = "createdAt";
const DEFAULT_SORT_ORDER: SortOrder = "desc";

function formatUser(user: ReviewUser | null): string {
	if (!user) return "—";
	return user.username || `#${user.id}`;
}

function formatDate(date: string): string {
	return dayjs(new Date(date)).format("DD-MM-YYYY HH:mm");
}

export function AiResponseReviews({ responseId }: AiResponseReviewsProps) {
	const { t } = useTranslation();
	const { fontSizeClass } = useFontSize();
	const [isOpen, setIsOpen] = useState(false);
	const [statusFilter, setStatusFilter] =
		useState<StatusFilter>(DEFAULT_STATUS);
	const [sortBy, setSortBy] = useState<SortBy>(DEFAULT_SORT_BY);
	const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);

	const { data, isLoading } = useAiResponseReviews(responseId, {
		status: statusFilter === "all" ? undefined : statusFilter,
		sortBy,
		sortOrder,
		enabled: isOpen,
	});

	const reviews: Review[] = data?.data ?? [];
	const totalCount: number = data?.count ?? 0;

	const filterOptions = [
		{
			value: "all" as StatusFilter,
			label: t("ai.reviews.filter.all", "Wszystkie"),
		},
		{
			value: "waiting" as StatusFilter,
			label: t("ai.reviews.filter.waiting", "Oczekujące"),
		},
		{
			value: "resolved" as StatusFilter,
			label: t("ai.reviews.filter.resolved", "Rozwiązane"),
		},
	];

	const sortOptions = [
		{
			sortBy: "createdAt" as SortBy,
			sortOrder: "desc" as SortOrder,
			label: t("ai.reviews.sort.newest", "Najnowsze"),
		},
		{
			sortBy: "createdAt" as SortBy,
			sortOrder: "asc" as SortOrder,
			label: t("ai.reviews.sort.oldest", "Najstarsze"),
		},
		{
			sortBy: "updatedAt" as SortBy,
			sortOrder: "desc" as SortOrder,
			label: t("ai.reviews.sort.updated", "Ostatnio zmienione"),
		},
		{
			sortBy: "resolvedAt" as SortBy,
			sortOrder: "desc" as SortOrder,
			label: t("ai.reviews.sort.resolved", "Ostatnio rozwiązane"),
		},
	];

	return (
		<Accordion
			expanded={isOpen}
			onChange={(_e, expanded) => setIsOpen(expanded)}
		>
			<AccordionSummary expandIcon={<ChevronDown className="icon-small" />}>
				<div className="flex items-center gap-8">
					<CircleAlert className="icon-small" />
					<span className={fontSizeClass}>
						{t("ai.reviews.title", "Uwagi od użytkowników")}
						{totalCount > 0 ? ` (${totalCount})` : ""}
					</span>
				</div>
			</AccordionSummary>
			<AccordionDetails>
				<div className="flex flex-col gap-16">
					<div className="flex items-center justify-end gap-8">
						<Filters<StatusFilter>
							options={filterOptions}
							value={statusFilter}
							onChange={setStatusFilter}
							defaultValue={DEFAULT_STATUS}
						/>
						<Sort<SortBy, SortOrder>
							options={sortOptions}
							sortBy={sortBy}
							sortOrder={sortOrder}
							onChange={(by, order) => {
								setSortBy(by);
								setSortOrder(order);
							}}
							defaultSortBy={DEFAULT_SORT_BY}
							defaultSortOrder={DEFAULT_SORT_ORDER}
						/>
					</div>

					{isLoading && (
						<span className={cx(fontSizeClass, "text-grey text-center")}>
							{t("ai.reviews.loading", "Ładowanie...")}
						</span>
					)}

					{!isLoading && reviews.length === 0 && (
						<span className={cx(fontSizeClass, "text-grey text-center")}>
							{t("ai.reviews.empty", "Brak uwag")}
						</span>
					)}

					{reviews.map((review) => (
						<ReviewItem key={review.id} review={review} />
					))}
				</div>
			</AccordionDetails>
		</Accordion>
	);
}

interface ReviewItemProps {
	review: Review;
}

interface StatusBadgeProps {
	status: ReviewStatus;
	tooltip?: string;
}

function StatusBadge({ status, tooltip }: StatusBadgeProps) {
	const { t } = useTranslation();
	const label =
		status === "resolved"
			? t("ai.reviews.status.resolved", "Rozwiązane")
			: t("ai.reviews.status.waiting", "Oczekuje");
	const variantClass =
		status === "resolved" ? "alert-success" : "alert-warning";

	const badge = (
		<span
			className={cx("size-12-light px-8 py-2 border-radius-12", variantClass)}
		>
			{label}
		</span>
	);

	if (!tooltip) {
		return badge;
	}

	return <Tooltip title={tooltip}>{badge}</Tooltip>;
}

function ReviewItem({ review }: ReviewItemProps) {
	const { t } = useTranslation();
	const { fontSizeClass } = useFontSize();
	const isAdmin = isStaff();
	const { mutateAsync: updateStatus, isPending: isUpdating } =
		useUpdateAiReviewStatus();

	const handleSetStatus = async (
		status: ReviewStatus,
		successKey: string,
		successFallback: string,
	) => {
		try {
			await updateStatus({ reviewId: review.id, status });
			toast.success(t(successKey, successFallback), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 3000,
			});
		} catch {
			toast.error(t("errors.somethingWentWrong"), {
				position: toast.POSITION.BOTTOM_LEFT,
				autoClose: 5000,
			});
		}
	};

	const handleResolve = () =>
		handleSetStatus(
			"resolved",
			"ai.reviews.markedResolved",
			"Oznaczono jako rozwiązane",
		);

	const handleRevertResolve = () =>
		handleSetStatus(
			"waiting",
			"ai.reviews.revertedToWaiting",
			"Cofnięto akceptację",
		);

	const showResolveButton = isAdmin && review.status === "waiting";
	const showRevertButton = isAdmin && review.status === "resolved";
	const isResolved = review.status === "resolved";

	const resolvedTooltip =
		isResolved && review.resolvedAt
			? t("ai.reviews.resolvedBy", "Rozwiązane przez {{user}} ({{date}})", {
					user: review.resolvedBy ?? "—",
					date: formatDate(review.resolvedAt),
				})
			: undefined;

	return (
		<div
			className={cx(
				"ai-review__card",
				isResolved && "ai-review__card--resolved",
			)}
		>
			<div className="user-opinion">
				<div className="user-opinion__meta">
					<span className={cx(fontSizeClass, "font-bold")}>
						{formatUser(review.user)}
					</span>
					<span className={cx("size-12-light", "text-grey")}>
						{formatDate(review.createdAt)}
					</span>
				</div>
				<StatusBadge status={review.status} tooltip={resolvedTooltip} />
			</div>
			<span className={fontSizeClass}>{review.review}</span>
			{(showResolveButton || showRevertButton) && (
				<div className="flex justify-end">
					{showResolveButton && (
						<PrimaryButton
							className="size-16-light"
							onClick={handleResolve}
							isLoading={isUpdating}
						>
							{t("ai.reviews.markAsResolved", "Oznacz jako rozwiązane")}
						</PrimaryButton>
					)}
					{showRevertButton && (
						<OutlinedPrimaryButton
							className="size-16-light"
							onClick={handleRevertResolve}
							isLoading={isUpdating}
						>
							{t("ai.reviews.revertResolve", "Cofnij akceptację")}
						</OutlinedPrimaryButton>
					)}
				</div>
			)}
		</div>
	);
}
